"use client";

import React, { useState, useEffect } from "react";

interface HeadingItem {
  id: string;
  text: string;
  level: 2 | 3;
}

interface TableOfContentsProps {
  content: string;
}

export function TableOfContents({ content }: TableOfContentsProps) {
  const [headings, setHeadings] = useState<HeadingItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const allHeadings: HeadingItem[] = [];
    let match: RegExpExecArray | null;

    const h2Regex = /<h2[^>]*id="([^"]+)"[^>]*>(.*?)<\/h2>/g;
    while ((match = h2Regex.exec(content)) !== null) {
      const text = match[2].replace(/<[^>]*>/g, "");
      allHeadings.push({ id: match[1], text, level: 2 });
    }

    const h3Regex = /<h3[^>]*id="([^"]+)"[^>]*>(.*?)<\/h3>/g;
    while ((match = h3Regex.exec(content)) !== null) {
      const text = match[2].replace(/<[^>]*>/g, "");
      allHeadings.push({ id: match[1], text, level: 3 });
    }

    allHeadings.sort((a, b) => {
      const idxA = content.indexOf(`id="${a.id}"`);
      const idxB = content.indexOf(`id="${b.id}"`);
      return idxA - idxB;
    });

    Promise.resolve().then(() => setHeadings(allHeadings));

    if (allHeadings.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: "0px 0px -60% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, observerOptions);

    allHeadings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });

    return () => {
      allHeadings.forEach((h) => {
        const el = document.getElementById(h.id);
        if (el) observer.unobserve(el);
      });
    };
  }, [content]);

  if (headings.length === 0) return null;

  const handleScrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveId(id);
    }
  };

  return (
    <aside className="bg-[var(--surface)] border border-[var(--border)] rounded-lg p-6 shadow-none flex flex-col gap-4 flex-1 min-h-0 card-blueprint text-left">
      <h3 className="font-heading-condensed font-extrabold text-base text-[var(--ink)] flex items-center gap-2 border-b border-[var(--line)] pb-3 flex-shrink-0 uppercase">
        <svg xmlns="http://www.w3.org/2055/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--accent)]">
          <line x1="8" y1="6" x2="21" y2="6"></line>
          <line x1="8" y1="12" x2="21" y2="12"></line>
          <line x1="8" y1="18" x2="21" y2="18"></line>
          <line x1="3" y1="6" x2="3.01" y2="6"></line>
          <line x1="3" y1="12" x2="3.01" y2="12"></line>
          <line x1="3" y1="18" x2="3.01" y2="18"></line>
        </svg>
        On This Page
      </h3>
      <nav className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto font-mono text-[11px] uppercase tracking-wider">
        {headings.map((heading) => {
          const isActive = activeId === heading.id;
          const isH2 = heading.level === 2;
          return (
            <button
              key={heading.id}
              onClick={() => handleScrollTo(heading.id)}
              className={`text-left py-1.5 border-l-2 pl-3 transition-all duration-150 cursor-pointer ${
                isActive
                  ? "border-[var(--accent)] text-[var(--accent)] font-bold translate-x-1"
                  : "border-[var(--line)] text-[var(--ink-soft)] hover:text-[var(--accent)] hover:border-[var(--accent)]/55"
              } ${isH2 ? "text-[11px]" : "text-[10px] ml-4 font-normal"}`}
            >
              {heading.text}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}
